import {LitElement, html, css} from 'lit';
import {connect} from 'pwa-helpers';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {msg} from '@lit/localize';
import familyConfig from '../../familyConfig';
import store from '../../store';
import {selectFamilyConfig} from '../../selectors';
import familyAttributes from '../../familyAttributes';

@customElement('family-list')
export class FamilyList extends connect(store)(LitElement) {

	static styles = css`

		.familyList {
			background-color: #ECECEC;
			display: flex;
			height: 70vh;
			flex-direction: row;
		}

		.familyFilter {
			background-color: var(--fix-color-black);
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;
			gap: var(--very-small-padding);;
			padding: var(--small-padding);
		}

		.image {
			aspect-ratio: 1;
			flex-grow: 0;
			flex-shrink: 0;
			background-color: #ECECEC;
			background-size: cover;
			transition: opacity 0.2s ease-in-out;
			transition-delay: 0s;
			opacity: 1;
		}
		@media (max-width: 1024px) or (max-aspect-ratio: 1/1) {
			.image {
				display: none
			}
		}
		.transitioning {
			transition-delay: 0.1s;
			opacity: 0;
		}
		.list {
			overflow-y: auto;
			overflow-x: hidden;
			flex-grow: 1;
			flex-shrink: 0;
		}

		.item {
			position: relative;
			min-width: 0;
			text-decoration: none;
			display: flex;
			flex-direction: row;
			justify-content: start;
			background-color: #fff;
			padding: var(--very-small-padding) var(--small-padding);
			border-bottom: 1px solid var(--fix-color-border);
			transition: background-color 0.4s ease;
		}

		.item:hover {
			background-color: #d8d8d8;
		}

		.label {
			color: var(--fix-color-black);
			flex-basis: 210px;
			flex-grow: 0;
			flex-shrink: 0;
		}

		.description {
			flex-basis: calc(100% - 210px);
			flex-grow: 1;
			flex-shrink: 0;
			color: var(--fix-color-black);
			opacity: 0.5;
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
		}

	`;

	@property()
	active = false;

	@property()
	hoveredFamily = null;

	@property()
	query = {};

	@property()
	transitioning = false;


	debounced = null;

	attributes = familyAttributes;

	constructor() {
		super();
		this.query = this.attributes.map(attribute => ({id: attribute.id, selected: []}));
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.host = state.status.host;
		this.base = state.status.baseUrl;
		this.lang = state.status.lang;
		this.region = state.status.region;
		this.families = familyConfig;
		if (this.lang && this.lang !== '') {
			// wait for language to be available
			// convert this.families object to array and put the objects ids into each item
			this.familiesArray = Object.entries(this.families)
				.map(([name, config]) => ({id: name, ...config}))
				.filter(family => family.hasWizard)
				.sort((a, b) => a.label[this.lang].localeCompare(b.label[this.lang]));
		} else {
			this.familiesArray = null;
		}
	}

	connectedCallback() {
		super.connectedCallback();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
	}

	familyUrl(family) {
		const region = this.region;
		const lang = this.lang;

		let route = '';
		if (this.host === 'https://selux.com' || this.host === 'https://www.selux.cdlx.dev') {
			route = `/${region}/${lang}/datahub?family=${family.id}`;
		} else {
			route = `/demo.php?region=${region}&lang=${lang}&family=${family.id}`;
		}
		return route;
	}

	familyImage(family) {
		if (family === null) return '';
		return this.host + this.base + 'line-assets/img/family/' + family.image;
	}

	onHover(family) {
		if (family === null) return;
		this.transitioning = false;
		this.hoveredFamily = family;
		clearInterval(this.debounced);
	}

	onHoverWish(family) {
		this.transitioning = true;
		clearInterval(this.debounced);
		this.debounced = setInterval(() => {
			this.onHover(family);
		}, 300);
	}
	onOptionSelected(detail) {
		this.query[detail.id] = detail.selected;
		this.query.forEach(queryItem => {
			if (queryItem.id === detail.id) queryItem.selected = detail.selected;
		});
		this.requestUpdate();
	}

	filteredFamilies() {
		return this.familiesArray.filter(family => {
			let match = true;
			this.query.forEach(queryItem => {
				if (
					queryItem.selected.length > 0 &&
					!family.attributes[queryItem.id].some(
						attribute => queryItem.selected.includes(attribute)
					)
				) match = false;
			});
			return match;
		});
	}

	familyDescription(family, attributeId) {
		const attribute = this.attributes.find(attr => attr.id === attributeId);
		//console.log('familyDescription', attributeId, family.attributes[attributeId], attribute);
		return family.attributes[attributeId]
			.map(facet => (attribute.options.find(option => option.value === facet)).label[this.lang]).join(', ');
	}

	render() {
		if (!this.familiesArray) return null;

		const currentConfig = selectFamilyConfig(this.state);
		let familyLabel = msg('Choose a Product Family');
		if (currentConfig && currentConfig?.label) {
			familyLabel = currentConfig?.label[this.lang];
		}
		return html`
			<div class="familyFilter">
				${repeat(
					this.attributes,
					attribute => attribute.id,
					attribute => html`
						<family-list-filter .attribute="${attribute}" @optionSelected="${(e) => this.onOptionSelected(e.detail)}"></family-list-filter>
					`)}
			</div>
			<div class="familyList">
				<div class="image ${classMap({transitioning: this.transitioning})}" style="background-image: url('${this.familyImage(this.hoveredFamily)}')">
				</div>
				<div class="list">
					${repeat(
						this.filteredFamilies(),
						(family) => family.id,
						(family) => html`
							<a class="item" @mouseenter="${() => this.onHoverWish(family)}" href="${this.familyUrl(family)}">
								<div class="label">${family.label[this.lang]}</div>
								<div class="description">${this.familyDescription(family, 'mounting')}</div>
							</a>
						`)

					}
				</div>
			</div>
		`;
	}
}
